import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, X } from "lucide-react";
import { Link } from "react-router-dom";

import { useSubscription } from "@/lib/subscription/useSubscription.hook";
import { SubscriptionService } from "@/lib/subscription/subscription.service";
import { PodcastService } from "@/lib/podcast/podcast.service";
import { AnalyticsService } from "@/lib/analytics/analytics.service";

const DISMISS_KEY = "trial_countdown_banner_dismissed";

/** Fallback price if the plans endpoint fails or Starter is missing. */
const STARTER_FALLBACK_PRICE = "€8.99";

function formatEuro(value: number): string {
	return `€${value.toFixed(2)}`;
}

function daysBetween(target: Date): number {
	const diffMs = target.getTime() - Date.now();
	return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Banner rendered in the app shell from day 5 of the trial onward
 * (`daysRemaining <= 2`). Dismissible via sessionStorage — reappears on next
 * reload until the trial actually ends.
 */
export function TrialCountdownBanner() {
	const { t } = useTranslation();
	const { subscriptionStatus } = useSubscription();
	const [dismissed, setDismissed] = useState(
		() => !!sessionStorage.getItem(DISMISS_KEY),
	);

	const isTrialing = subscriptionStatus === "TRIALING";

	const { data: summaryStats } = useQuery({
		queryKey: ["podcast-summary-stats-banner"],
		queryFn: () => PodcastService.getSummaryStats(),
		enabled: isTrialing,
		staleTime: 1000 * 60 * 5,
	});

	const { data } = useQuery({
		queryKey: ["subscription-details-banner"],
		queryFn: () => SubscriptionService.getSubscriptionDetails(),
		enabled: isTrialing,
		staleTime: 1000 * 60 * 5,
	});

	const { data: plans } = useQuery({
		queryKey: ["subscription-plans-banner"],
		queryFn: () => SubscriptionService.getPlans(),
		enabled: isTrialing,
		staleTime: 1000 * 60 * 30,
	});

	const starterPrice = plans?.find((p) => p.tier === "STARTER")?.price ?? null;
	const keepFromPrice =
		starterPrice && starterPrice > 0
			? formatEuro(starterPrice)
			: STARTER_FALLBACK_PRICE;

	useEffect(() => {
		if (!isTrialing) {
			setDismissed(false);
			sessionStorage.removeItem(DISMISS_KEY);
		}
	}, [isTrialing]);

	// Rich loss-aversion copy when the user has actually made podcasts.
	const useRichCopy = (summaryStats?.podcastCount ?? 0) > 0;

	useEffect(() => {
		if (isTrialing && !dismissed && data?.currentPeriodEnd) {
			const days = daysBetween(data.currentPeriodEnd);
			if (days <= 2 && days >= 0) {
				AnalyticsService.track("trial_banner_variant_shown", {
					variant: useRichCopy ? "rich" : "generic",
				});
			}
		}
	}, [isTrialing, dismissed, data?.currentPeriodEnd, useRichCopy]);

	if (!isTrialing || dismissed || !data?.currentPeriodEnd) return null;

	const daysRemaining = daysBetween(data.currentPeriodEnd);
	if (daysRemaining > 2 || daysRemaining < 0) return null;

	const handleDismiss = () => {
		setDismissed(true);
		sessionStorage.setItem(DISMISS_KEY, "1");
	};

	const richMessage =
		daysRemaining <= 0
			? t("trial.banner.richEndingToday", {
					price: keepFromPrice,
					count: summaryStats?.podcastCount ?? 0,
					minutes: summaryStats?.totalDurationMinutes ?? 0,
				})
			: t("trial.banner.richDaysLeft", {
					count: daysRemaining,
					price: keepFromPrice,
					podcastCount: summaryStats?.podcastCount ?? 0,
					minutes: summaryStats?.totalDurationMinutes ?? 0,
				});

	const genericMessage =
		daysRemaining <= 0
			? t("trial.banner.endingToday", { price: keepFromPrice })
			: t("trial.banner.daysLeft", {
					count: daysRemaining,
					price: keepFromPrice,
				});

	return (
		<div className="w-full bg-emerald-500/10 border-b border-emerald-500/20 text-emerald-200">
			<div className="mx-auto max-w-7xl px-4 py-2 flex items-center justify-between gap-3">
				<div className="flex items-center gap-2 text-sm">
					<Sparkles className="h-4 w-4 text-emerald-400 flex-shrink-0" />
					<span>{useRichCopy ? richMessage : genericMessage}</span>
				</div>
				<div className="flex items-center gap-2">
					<Link
						to="/pricing"
						className="text-xs font-medium underline-offset-4 hover:underline text-emerald-100"
					>
						{t("trial.banner.manage")}
					</Link>
					<button
						type="button"
						onClick={handleDismiss}
						aria-label={t("common.close")}
						className="rounded p-1 hover:bg-emerald-500/10"
					>
						<X className="h-3.5 w-3.5" />
					</button>
				</div>
			</div>
		</div>
	);
}
